
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BulletinCard from './BulletinCard';
import BulletinMetaTags from '../meta/BulletinMetaTags';
import BulletinBackButton from './BulletinBackButton';
import BulletinInspectionTools from './BulletinInspectionTools';
import RelicInspectView from './relic/RelicInspectView';
import { useIsMobile } from '@/hooks/use-mobile';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface BulletinDetailViewProps {
  bulletin: any;
  selectedId: string;
  onLike: () => void;
  onDelete: () => void;
  onClose: () => void;
}

/**
 * Component for displaying a bulletin in fullscreen detail view
 */
const BulletinDetailView: React.FC<BulletinDetailViewProps> = ({
  bulletin,
  selectedId,
  onLike,
  onDelete,
  onClose
}) => {
  const isMobile = useIsMobile();
  const [isLoaded, setIsLoaded] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const [isArtstream, setIsArtstream] = useState<boolean>(bulletin?.is_artstream ?? false);
  
  console.log(`[BulletinDetailView] Render - selectedId: ${selectedId}, isMobile: ${isMobile}, isRelic: ${bulletin?.is_relic}`, {
    bulletinId: bulletin?.id,
    isRelic: bulletin?.is_relic,
    savedAt: bulletin?.saved_at,
    fullBulletin: bulletin
  });
  
  // Enhanced relic detection - check both is_relic flag and saved_at field
  const isRelic = bulletin?.is_relic === true || (bulletin?.saved_at && !bulletin?.expires_at);
  
  console.log(`🏛️ [BulletinDetailView] Relic detection for ${bulletin?.id}:`, {
    is_relic: bulletin?.is_relic,
    saved_at: bulletin?.saved_at,
    expires_at: bulletin?.expires_at,
    finalIsRelic: isRelic
  });
  
  // Check if this is a relic and render the specialized relic view
  if (isRelic) {
    console.log(`✅ [BulletinDetailView] Rendering RelicInspectView for bulletin ${bulletin?.id}`);
    return (
      <RelicInspectView
        bulletin={{ ...bulletin, is_relic: true }}
        selectedId={selectedId}
        onClose={onClose}
      />
    );
  }
  
  // Track touch start position for swipe-to-dismiss
  const touchStartY = React.useRef<number | null>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);
  
  // Force the bulletin to be fully loaded before allowing it to be dismissed
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log(`[BulletinDetailView] Setting isLoaded to true`);
      setIsLoaded(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Create a wrapper for onDelete that ensures proper cleanup after deletion
  const handleDelete = () => {
    console.log(`[BulletinDetailView] Handling delete`);
    onDelete();
    onClose();
  };

  const canEdit = !!user?.id && (user.id === bulletin?.user_id);
  const handleSaveArtstream = async () => {
    try {
      const { error } = await supabase
        .from('bulletins')
        .update({ is_artstream: isArtstream })
        .eq('id', bulletin.id);
      if (error) throw error;
      toast({ title: 'Updated', description: isArtstream ? 'Post will appear in Artstream.' : 'Post removed from Artstream.' });
    } catch (err: any) {
      console.error('[BulletinDetailView] update is_artstream failed', err);
      toast({ title: 'Update failed', description: err?.message || 'Could not update post.', variant: 'destructive' });
    }
  };

  // Handle touch events for swipe-to-dismiss (mobile only)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMobile && isLoaded) {
      touchStartY.current = e.touches[0].clientY;
      console.log(`[BulletinDetailView] Touch start at Y: ${touchStartY.current}`);
      
      if (e.currentTarget === overlayRef.current) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isMobile && touchStartY.current !== null && isLoaded) {
      const touchEndY = e.changedTouches[0].clientY;
      const diffY = touchEndY - touchStartY.current;
      
      console.log(`[BulletinDetailView] Touch end - diffY: ${diffY}`);
      
      // If swiped down more than 100px, close the detail view
      if (diffY > 100) {
        console.log('Swipe down detected, closing detail view');
        onClose();
      }
      
      touchStartY.current = null;
    }
  };

  // Handle direct tap on overlay to close
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current && isLoaded) {
      console.log('Overlay click detected, closing detail view');
      onClose();
    }
  };
  
  return (
    <>
      {/* Background overlay with proper z-index */}
      <AnimatePresence>
        <motion.div
          ref={overlayRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
          className="fixed inset-0 bg-black/60"
          style={{ 
            zIndex: 40,
            touchAction: 'none',
            pointerEvents: isLoaded ? 'auto' : 'none'
          }}
          transition={{ duration: 0.15 }}
          data-testid="bulletin-detail-overlay"
        />
      </AnimatePresence>

      {/* Detail view container with consistent positioning */}
      <div 
        className="fixed inset-0 flex items-center justify-center"
        style={{ 
          zIndex: 50,
          pointerEvents: 'none',
          padding: isMobile ? '0' : '1rem' // Use inline style for consistent behavior
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Add meta tags for social sharing when a bulletin is viewed */}
        <BulletinMetaTags bulletin={bulletin} />
        
        {/* Always show back button on mobile for easy navigation */}
        {isMobile && (
          <div 
            style={{ 
              pointerEvents: 'auto',
              position: 'fixed',
              top: '0.5rem',
              left: '0.5rem',
              zIndex: 70
            }}
          >
            <BulletinBackButton onClose={onClose} />
          </div>
        )}
        
        <motion.div
          className="relative w-full h-full flex items-center justify-center"
          style={{ 
            pointerEvents: 'auto',
            zIndex: 60,
            maxWidth: isMobile ? 'none' : '56rem', // 896px - consistent max width for desktop
            maxHeight: isMobile ? 'none' : '90vh' // Prevent overflow on desktop
          }}
          initial={{ opacity: 0, scale: isMobile ? 1 : 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: isMobile ? 1 : 0.95 }}
          transition={{ duration: isMobile ? 0.2 : 0.3 }}
        >
          {/* Author controls: Is it art? toggle */}
          {canEdit && (
            <div className="absolute top-3 right-3 flex items-center gap-3 rounded-md border border-border/50 bg-[#171718] px-3 py-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm">Is it art?</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-muted-foreground underline cursor-help">What is this?</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-sm">If enabled, this post will be featured in Artstream. Turn off for conversations or non-art posts.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Switch checked={isArtstream} onCheckedChange={setIsArtstream} />
              <button
                className="text-xs px-2 py-1 rounded border border-border bg-[#222] hover:bg-[#2a2a2a]"
                onClick={handleSaveArtstream}
              >
                Save
              </button>
            </div>
          )}
          {/* Add inspection tools for admins */}
          <BulletinInspectionTools 
            bulletin={bulletin} 
            isSelected={true}
          />
          
          <div className="w-full h-full flex items-center justify-center">
            <BulletinCard
              bulletin={bulletin}
              onLike={onLike}
              onDelete={handleDelete}
              size="large"
              isSelected={true}
              onClose={onClose}
            />
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default BulletinDetailView;
